import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { useAuth } from '../auth/AuthContext';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';
import { usePermissions } from '../../hooks/usePermissions';
import { createAssociatedNote, deleteNote, fetchAllCustomerNotes, fetchAllOpportunityNotes } from '../../utils/associationUtils';
import { format } from 'date-fns';
import Tooltip from './Tooltip';
import { useGamification } from '../gamification/GamificationContext';

/**
 * UnifiedNotes Component
 * 
 * Displays notes from all sources for a given entity with cross-referencing.
 * Features:
 * - Real-time updates via Firestore subscriptions (except customer which uses fetch)
 * - For customers: Shows ALL notes from related opportunities, tasks, orders, contacts
 * - Create notes with automatic association
 * - Delete notes with permission checks
 * - Group by source type toggle
 * - Visual source badges
 * 
 * @param {string} entityType - 'customer' | 'opportunity' | 'order' | 'contact' | 'task'
 * @param {string} entityId - ID of the entity
 * @param {number} maxNotes - Maximum number of notes to display (default: 100)
 * @param {function} onNotesLoaded - Callback fired when notes are loaded with count
 */
const ENTITY_EDIT_PERMISSIONS = {
  customer: FEATURE_PERMISSIONS.CUSTOMER_EDIT,
  contact: FEATURE_PERMISSIONS.CONTACT_EDIT,
  opportunity: FEATURE_PERMISSIONS.OPP_EDIT,
  task: FEATURE_PERMISSIONS.TASK_EDIT,
};

const UnifiedNotes = ({ entityType, entityId, maxNotes = 100, onNotesLoaded, refreshTrigger = 0 }) => {
  const { currentUser } = useAuth();
  const { recordActivity } = useGamification();
  const { can } = usePermissions(currentUser);
  const canEditEntity = can(ENTITY_EDIT_PERMISSIONS[entityType]);
  const canAddNote = canEditEntity || can(FEATURE_PERMISSIONS.NOTE_ADD);
  const canDeleteAny = can(FEATURE_PERMISSIONS.NOTE_DELETE_ANY);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNoteText, setNewNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [groupBySource, setGroupBySource] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0); // Used to trigger refetch for unified views

  // Function to load all notes for unified view (customer or opportunity)
  const loadUnifiedNotes = useCallback(async () => {
    if (!entityId) return;
    if (entityType !== 'customer' && entityType !== 'opportunity') return;
    
    setLoading(true);
    try {
      let allNotes;
      if (entityType === 'customer') {
        allNotes = await fetchAllCustomerNotes(entityId, maxNotes);
      } else if (entityType === 'opportunity') {
        allNotes = await fetchAllOpportunityNotes(entityId, maxNotes);
      }
      setNotes(allNotes || []);
      if (onNotesLoaded) {
        onNotesLoaded(allNotes?.length || 0);
      }
    } catch (err) {
      console.error(`Failed to fetch all ${entityType} notes:`, {
        operation: `FETCH_ALL_${entityType.toUpperCase()}_NOTES`,
        entityId,
        error: err.message,
        timestamp: new Date().toISOString()
      });
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
    // Note: onNotesLoaded intentionally not in deps to prevent infinite loops from parent state updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, maxNotes]);

  // Real-time subscription to notes (for simple entities: order, contact, task)
  // for customer/opportunity: Uses unified fetch for complete view including related entities
  useEffect(() => {
    if (!entityType || !entityId) {
      setNotes([]);
      setLoading(false);
      if (onNotesLoaded) {
        onNotesLoaded(0);
      }
      return;
    }

    // for customer and opportunity entity types, use the unified fetch functions
    if (entityType === 'customer' || entityType === 'opportunity') {
      loadUnifiedNotes();
      return; // No cleanup needed - not a subscription
    }

    setLoading(true);
    
    // Build query based on entity type to use proper indexed fields
    let q;
    if (entityType === 'order') {
      q = query(
        collection(db, collectionPath('notes')),
        where('associations.orderId', '==', entityId),
        orderBy('createdAt', 'desc'),
        limit(maxNotes)
      );
    } else if (entityType === 'contact') {
      q = query(
        collection(db, collectionPath('notes')),
        where('associations.contactId', '==', entityId),
        orderBy('createdAt', 'desc'),
        limit(maxNotes)
      );
    } else if (entityType === 'task') {
      q = query(
        collection(db, collectionPath('notes')),
        where('associations.taskId', '==', entityId),
        orderBy('createdAt', 'desc'),
        limit(maxNotes)
      );
    } else {
      // Fallback for unknown entity types
      setError('Invalid entity type');
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setNotes(notesData);
        setLoading(false);
        
        // Notify parent of notes count
        if (onNotesLoaded) {
          onNotesLoaded(notesData.length);
        }
      },
      (err) => {
        console.error('Failed to fetch notes:', {
          operation: 'FETCH_NOTES',
          entityType,
          entityId,
          error: err.message,
          code: err.code,
          timestamp: new Date().toISOString()
        });
        setError('Failed to load notes');
        setLoading(false);
      }
    );

    return () => unsubscribe();
    // Note: loadUnifiedNotes not in deps to avoid double-fetch when refreshKey changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, maxNotes, refreshKey]);

  // Refresh unified notes (customer/opportunity) when refreshKey or parent refreshTrigger changes
  useEffect(() => {
    if ((entityType === 'customer' || entityType === 'opportunity') && refreshKey > 0) {
      loadUnifiedNotes();
    }
  }, [entityType, refreshKey, loadUnifiedNotes]);

  // External refresh trigger from parent component (e.g., tab navigation)
  useEffect(() => {
    if (refreshTrigger > 0 && (entityType === 'customer' || entityType === 'opportunity')) {
      loadUnifiedNotes();
    }
  }, [refreshTrigger, entityType, loadUnifiedNotes]);

  // Filter notes by search text
  const filteredNotes = useMemo(() => {
    if (!searchText.trim()) return notes;
    
    const searchLower = searchText.toLowerCase();
    return notes.filter(note => {
      const text = (note.text || '').toLowerCase();
      const user = (note.user || '').toLowerCase();
      return text.includes(searchLower) || user.includes(searchLower);
    });
  }, [notes, searchText]);

  // Group notes by source type
  const groupedNotes = useMemo(() => {
    const groups = {
      customer: [],
      opportunity: [],
      order: [],
      contact: [],
      task: []
    };

    filteredNotes.forEach(note => {
      const source = note.sourceEntityType;
      if (groups[source]) {
        groups[source].push(note);
      }
    });

    return groups;
  }, [filteredNotes]);

  // Handle creating a new note
  const handleCreateNote = async (e) => {
    e.preventDefault();
    
    if (!newNoteText.trim()) {
      setError('Note text is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const noteResult = await createAssociatedNote(
        {
          text: newNoteText.trim(),
          sourceEntityType: entityType,
          sourceEntityId: entityId,
          attachments: []
        },
        currentUser,
        { [`${entityType}Id`]: entityId }
      );

      setNewNoteText('');
      
      // Gamification: Track note creation (with anti-abuse validation)
      try {
        await recordActivity('NOTE_CREATED', {
          entityId: noteResult || entityId,
          source: 'manual',
          entityData: { content: newNoteText.trim() }
        });
      } catch (gamErr) {
        console.warn('Gamification tracking failed:', gamErr);
      }
      
      // For unified view entity types (customer/opportunity), trigger a refresh since we don't have real-time subscription
      if (entityType === 'customer' || entityType === 'opportunity') {
        setRefreshKey(prev => prev + 1);
      }
      // Other entity types will update via real-time subscription
    } catch (err) {
      console.error('Failed to create note:', {
        operation: 'CREATE_NOTE',
        entityType,
        entityId,
        userId: currentUser?.uid,
        error: err.message,
        timestamp: new Date().toISOString()
      });
      setError('Failed to create note');
    } finally {
      setSaving(false);
    }
  };

  // Handle deleting a note
  const handleDeleteNote = async (noteId, noteCreatorId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteNote(noteId, currentUser);
      
      // For unified view entity types (customer/opportunity), trigger a refresh since we don't have real-time subscription
      if (entityType === 'customer' || entityType === 'opportunity') {
        setRefreshKey(prev => prev + 1);
      }
      // Other entity types will update via real-time subscription
    } catch (err) {
      console.error('Failed to delete note:', {
        operation: 'DELETE_NOTE',
        noteId,
        userId: currentUser?.uid,
        error: err.message,
        timestamp: new Date().toISOString()
      });
      setError(err.message || 'Failed to delete note');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Notes
          </h3>
          {/* For unified views (customer/opportunity), show count since it includes notes from related entities */}
          {(entityType === 'customer' || entityType === 'opportunity') && notes.length > 0 && (
            <span className="text-sm text-gray-500">
              ({notes.length} from all related entities)
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          {/* Refresh button for unified views (no real-time subscription) */}
          {(entityType === 'customer' || entityType === 'opportunity') && (
            <Tooltip text="Refresh notes from all related entities">
              <button
                type="button"
                onClick={() => setRefreshKey(prev => prev + 1)}
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </Tooltip>
          )}
          
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={groupBySource}
              onChange={(e) => setGroupBySource(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Group by source
          </label>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Create note form - hidden for read-only users */}
      {canAddNote && (
        <form onSubmit={handleCreateNote} className="space-y-2">
          <textarea
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            placeholder={`Add a note to this ${entityType}...`}
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={3}
            disabled={saving}
          />
          
          <div className="flex justify-end">
            <Tooltip text="Create a new note">
              <button
                type="submit"
                disabled={saving || !newNoteText.trim()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Add Note'}
              </button>
            </Tooltip>
          </div>
        </form>
      )}

      {/* Search filter */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search notes..."
            className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
              searchText ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
            }`}
          />
        </div>
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Clear
          </button>
        )}
      </div>

      {/* Notes list */}
      {filteredNotes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {searchText ? `No notes found matching "${searchText}"` : 'No notes yet. Add one above!'}
        </div>
      ) : (
        <>
          {searchText && (
            <div className="text-sm text-gray-600 mb-2">
              Showing {filteredNotes.length} of {notes.length} notes
            </div>
          )}
          {groupBySource ? (
            <GroupedNotesView 
              groupedNotes={groupedNotes}
              onDelete={handleDeleteNote}
              currentUser={currentUser}
              canEditEntity={canEditEntity}
              canDeleteAny={canDeleteAny}
            />
          ) : (
            <div className="space-y-3">
              {filteredNotes.map(note => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onDelete={handleDeleteNote}
                  currentUser={currentUser}
                  canEditEntity={canEditEntity}
                  canDeleteAny={canDeleteAny}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Grouped Notes View
 */
const GroupedNotesView = ({ groupedNotes, onDelete, currentUser, canEditEntity, canDeleteAny }) => {
  return (
    <div className="space-y-6">
      {Object.entries(groupedNotes)
        .filter(([_, notes]) => notes.length > 0)
        .map(([source, notes]) => (
          <div key={source} className="space-y-2">
            <h4 className="text-md font-semibold text-gray-700 capitalize flex items-center gap-2">
              {getSourceIcon(source)}
              {source} Notes ({notes.length})
            </h4>
            <div className="space-y-2 pl-4 border-l-2 border-gray-200">
              {notes.map(note => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onDelete={onDelete}
                  currentUser={currentUser}
                  canEditEntity={canEditEntity}
                  canDeleteAny={canDeleteAny}
                  hideSourceBadge
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

/**
 * Individual Note Card
 */
const NoteCard = ({ note, onDelete, currentUser, canEditEntity = false, canDeleteAny = false, hideSourceBadge = false }) => {
  const isOwnNote = currentUser?.uid === note.createdBy;
  const canDelete = isOwnNote || canDeleteAny;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          {/* Header info */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">
              {note.createdByName || 'Unknown User'}
            </span>
            <span>•</span>
            <span>
              {note.createdAt?.toDate 
                ? format(note.createdAt.toDate(), 'MMM d, yyyy h:mm a')
                : 'Unknown date'}
            </span>
            
            {!hideSourceBadge && (
              <>
                <span>•</span>
                <span className={`capitalize px-2 py-0.5 rounded text-xs font-medium ${getSourceBadgeClass(note.sourceEntityType)}`}>
                  {getSourceIcon(note.sourceEntityType)} {note.sourceEntityType}
                </span>
              </>
            )}
          </div>

          {/* Note text */}
          <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
            {note.text}
          </p>

          {/* Association badges */}
          {note.associations && (
            <div className="flex flex-wrap gap-2 pt-2">
              {note.associations.customerName && (
                <Tooltip text={`customer: ${note.associations.customerName}`}>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded flex items-center gap-1">
                    🏢 {note.associations.customerName}
                  </span>
                </Tooltip>
              )}
              {note.associations.opportunityNumberLabel && (
                <Tooltip text={`Opportunity: ${note.associations.opportunityNumberLabel}`}>
                  <span className="text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                      <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" fill="#1dc6faff" stroke="#038cb6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {note.associations.opportunityNumberLabel}
                  </span>
                </Tooltip>
              )}
              {note.associations.orderNumber && (
                <Tooltip text={`Order: ${note.associations.orderNumber}`}>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center gap-1">
                    📦 {note.associations.orderNumber}
                  </span>
                </Tooltip>
              )}
              {note.associations.contactName && (
                <Tooltip text={`Contact: ${note.associations.contactName}`}>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded flex items-center gap-1">
                    👤 {note.associations.contactName}
                  </span>
                </Tooltip>
              )}
            </div>
          )}

          {/* Migration indicator */}
          {note.migratedFrom && (
            <div className="text-xs text-gray-400 italic">
              Migrated from legacy system
            </div>
          )}
        </div>

        {/* Delete button */}
        {canDelete && (
          <Tooltip text="Delete this note">
            <button
              onClick={() => onDelete(note.id, note.createdBy)}
              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors flex-shrink-0"
              aria-label="Delete note"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

/**
 * Get icon for source type
 */
const getSourceIcon = (sourceType) => {
  if (sourceType === 'opportunity') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 inline-block" viewBox="0 0 24 24" fill="none">
        <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" fill="#1dc6faff" stroke="#038cb6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (sourceType === 'task') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 inline-block" fill="none" viewBox="0 0 24 24">
        <rect x="5" y="4" width="14" height="16" rx="2" stroke="#10B981" strokeWidth="2" fill="#D1FAE5"/>
        <path d="M8 9l2 2 4-4" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="9" y1="14" x2="16" y2="14" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
        <line x1="9" y1="17" x2="13" y2="17" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  }
  const icons = {
    customer: '🏢',
    order: '📦',
    contact: '👤'
  };
  return icons[sourceType] || '📝';
};

/**
 * Get badge color class for source type
 */
const getSourceBadgeClass = (sourceType) => {
  const classes = {
    customer: 'bg-gray-100 text-gray-800',
    opportunity: 'bg-cyan-100 text-cyan-800',
    order: 'bg-blue-100 text-blue-800',
    contact: 'bg-purple-100 text-purple-800',
    task: 'bg-emerald-100 text-emerald-800'
  };
  return classes[sourceType] || 'bg-gray-100 text-gray-800';
};

export default UnifiedNotes;






